import {
  ChatActionsContainer,
  ChatMainContainer,
  ChatScroller,
  PromptContainer,
  CHAT_COLUMN_MAX_WIDTH,
} from "./StyledContainers/StyledContainers";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  groupMessages,
  isUserMessages as userMessageExists,
} from "./ChatStreaming/services/chatHelpers";

import { Box } from "@mui/material";
import ChatContext from "./context/ChatContext";
import ChatErrorMessage from "./PromptInput/ChatErrorMessage";
import ChatMessageGroup from "./ChatMessage/ChatMessageGroup";
import ChatMessageSkeleton from "./ChatMessage/ChatMessageSkeleton";
import {
  ChatMessageWithParts,
  ImageContent,
  SubAgentEvent,
} from "../../views/assistant/types";
import { ErrorMetadata } from "./types/chatTypes";

type ChatContainerType = {
  dialogueId?: number;
  /** Compact tool rendering — see `ChatContextType.compactToolCalls`. */
  compactToolCalls?: boolean;
  /** Conversation only — see `ChatContextType.transcriptOnly`. */
  transcriptOnly?: boolean;
  /** Self-sent user lines — see `ChatContextType.hideUserMessage`. */
  hideUserMessage?: (
    message: ChatMessageWithParts,
    groupIndex: number,
  ) => boolean;
  dialogue: ChatMessageWithParts[];
  ChatPromptInput: React.ReactNode;
  AssistantTextMetadata?: React.FC<any>;
  AssistantTextMetadataProps?: any;
  MoreAssistantActions?: React.FC<any>;
  MoreAssistantActionsProps?: any;
  CustomAssistantText?: React.FC<any>;
  CustomAssistantTextProps?: any;
  CustomLoadingIndicator?: React.ComponentType;
  InitialPrompt?: React.ReactNode;
  /** If true, render `InitialPrompt` even before the first user message
   *  (default: only show after the user has sent a message). Opt-in so
   *  existing consumers' behavior is unchanged. */
  alwaysShowInitialPrompt?: boolean;
  /** Accepted for caller compat (SidebarChatPanel); scrolling is handled by
   *  the intent-gesture stick-to-bottom follow, which auto-scrolls unless the
   *  user has scrolled up to read. */
  autoScrollToBottom?: boolean;
  isChatConnected: boolean;
  isChatEnded: boolean;
  isChatLoading: boolean;
  /** True while the current assistant turn is still active, including model
   *  generation and any pending tool execution. Used to disable the "Update"
   *  button on edited user messages. */
  isTurnActive?: boolean;
  chatStreamError: string;
  isChatConnectionError: boolean;
  isChatMessageStreamStopped: boolean;
  errorMetadata?: ErrorMetadata | null;
  SplashScreen?: React.ReactNode;
  BackgroundComponent?: React.ReactNode;
  /** Rendered at the top of the scroller only while the conversation has no
   *  user messages. A richer empty-state slot than `BackgroundComponent` for
   *  surfaces that want an interactive splash (e.g. project-chat suggestions).
   *  Opt-in: unset renders nothing. */
  EmptyState?: React.ReactNode;
  startNewChat: () => void;
  resendUserMessage: (
    id: string,
    text: string,
    images?: ImageContent[],
  ) => void;
  regenerateResponse: (index: string) => void;
  setAsPreferredMessage: (id: string) => void;
  /** Retry the SSE connection after a transport error */
  onRetryConnection?: () => void;
  /** Active sub-agents for inline tracking (one entry per delegated agent) */
  activeSubAgents?: {
    dialogueId: number;
    personaName: string;
    status: string;
    parentToolCallId?: string;
    messages: SubAgentEvent[];
    startedAt?: number;
    endedAt?: number;
  }[];
};

/**
 * ChatContainer Component
 * The `ChatContainer` component is responsible for managing the chat interface.
 * It provides a context (`ChatContext`) that contains the state and functions necessary for handling
 * chat-related operations, including streaming messages from a server and managing a dialogue session.
 *
 */

const ChatContainer = ({
  dialogueId,
  compactToolCalls = true,
  transcriptOnly = false,
  hideUserMessage,
  dialogue,
  InitialPrompt,
  AssistantTextMetadata,
  AssistantTextMetadataProps,
  ChatPromptInput,
  MoreAssistantActions,
  MoreAssistantActionsProps,
  CustomAssistantText,
  CustomAssistantTextProps,
  CustomLoadingIndicator,
  alwaysShowInitialPrompt = false,
  isChatConnected,
  isChatLoading,
  isTurnActive = false,
  chatStreamError,
  isChatConnectionError,
  isChatMessageStreamStopped,
  errorMetadata,
  SplashScreen,
  BackgroundComponent,
  EmptyState,
  startNewChat,
  resendUserMessage,
  regenerateResponse,
  setAsPreferredMessage,
  onRetryConnection,
  activeSubAgents,
}: ChatContainerType) => {
  const groupedMessages = useMemo(() => groupMessages(dialogue), [dialogue]);

  const isUserMessages = useMemo(() => {
    return userMessageExists(dialogue);
  }, [dialogue]);

  // ── Stick-to-bottom auto-scroll ──
  // Follow streaming content automatically, but the moment the user scrolls
  // up on their own, stop fighting them; re-engage when they return to the
  // bottom or send a new message.
  //
  // Disengaged only by user intent (wheel-up, touch-drag, upward scrollTop
  // move); content growth fires no `scroll`, so streaming can't drop follow.
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const followRef = useRef(true);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  // Re-engage only at the true bottom (a loose band would instantly undo the
  // disengage); at-bottom wins over movedUp so scrollTop clamping can't drop follow.
  const prevScrollTopRef = useRef(0);
  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const movedUp = el.scrollTop < prevScrollTopRef.current;
    prevScrollTopRef.current = el.scrollTop;
    if (distanceFromBottom < 2) {
      followRef.current = true;
    } else if (movedUp) {
      followRef.current = false;
    }
  }, []);

  // "Let me read" gestures disengage follow. No distance gate: the gesture
  // fires before the scroll lands, while the view is still at the bottom.
  const handleUserScrollIntent = useCallback((e: WheelEvent | Event) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Unscrollable content: no scroll event would ever re-engage follow.
    if (el.scrollHeight <= el.clientHeight) return;
    const isUpward = "deltaY" in e ? (e as WheelEvent).deltaY < 0 : true;
    if (isUpward) followRef.current = false;
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleUserScrollIntent, { passive: true });
    el.addEventListener("touchmove", handleUserScrollIntent, { passive: true });
    return () => {
      el.removeEventListener("wheel", handleUserScrollIntent);
      el.removeEventListener("touchmove", handleUserScrollIntent);
    };
  }, [handleUserScrollIntent, isUserMessages]);

  // Any content growth (streamed tokens, tool cards, images loading) keeps
  // the view pinned to the newest content while follow is engaged.
  useEffect(() => {
    const el = scrollerRef.current;
    const content = el?.firstElementChild;
    if (!el || !content) return;
    const ro = new ResizeObserver(() => {
      if (followRef.current) scrollToBottom();
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [scrollToBottom, isUserMessages]);

  // Sending a message is an explicit "take me to the conversation" action —
  // re-engage follow even if the user had scrolled up to read history.
  const lastMessageType = dialogue[dialogue.length - 1]?.type;
  useEffect(() => {
    if (lastMessageType === "User") {
      followRef.current = true;
      scrollToBottom("smooth");
    }
  }, [dialogue.length, lastMessageType, scrollToBottom]);

  return (
    <ChatContext.Provider
      value={{
        dialogueId,
        compactToolCalls,
        transcriptOnly,
        hideUserMessage,
        chatErrorShown: Boolean(chatStreamError || isChatConnectionError),
        MoreAssistantActions,
        MoreAssistantActionsProps,
        CustomAssistantText,
        CustomAssistantTextProps,
        AssistantTextMetadata,
        AssistantTextMetadataProps,
        activeSubAgents,
      }}
    >
      {/* ChatMainContainer: Main container for the entire chat interface */}
      <ChatMainContainer>
        {/* ChatActionsContainer: Container for chat action buttons */}
        <ChatActionsContainer></ChatActionsContainer>
        {/* ChatScroller: Scrollable area where chat messages are displayed */}
        {!isUserMessages && BackgroundComponent}
        <ChatScroller ref={scrollerRef} onScroll={handleScroll}>
          <Box
            data-testid="aa-chat-messages"
            sx={{ maxWidth: CHAT_COLUMN_MAX_WIDTH, width: "100%" }}
          >
            {InitialPrompt && (alwaysShowInitialPrompt || isUserMessages) && (
              <Box width={"100%"}>{InitialPrompt}</Box>
            )}
            {EmptyState && !isUserMessages && (
              <Box width={"100%"}>{EmptyState}</Box>
            )}
            {/* Show typing indicator at the top when starting fresh conversation (AtomAssist talks first) */}
            {!isUserMessages && isChatLoading && CustomLoadingIndicator && (
              <CustomLoadingIndicator />
            )}
            {groupedMessages?.map((messageGroup, messageGroupIdx) => {
              const isLast = messageGroupIdx === groupedMessages.length - 1;
              return (
                <React.Fragment key={messageGroupIdx}>
                  <ChatMessageGroup
                    data-testid={`aa-msg-group-${messageGroup?.[0]?.id ?? messageGroupIdx}`}
                    dialogue={dialogue}
                    messageGroup={messageGroup}
                    messageGroupIdx={messageGroupIdx}
                    isChatConnected={isChatConnected}
                    isTurnActive={isTurnActive}
                    isChatMessageStreamStopped={isChatMessageStreamStopped}
                    resendUserMessage={resendUserMessage}
                    regenerateResponse={regenerateResponse}
                    setAsPreferredMessage={setAsPreferredMessage}
                  />
                  {isLast &&
                    isChatLoading &&
                    (CustomLoadingIndicator ? (
                      <CustomLoadingIndicator />
                    ) : (
                      <ChatMessageSkeleton />
                    ))}
                  {isLast && (
                    <ChatErrorMessage
                      dialogue={dialogue}
                      chatStreamError={chatStreamError}
                      isChatConnectionError={isChatConnectionError}
                      errorMetadata={errorMetadata}
                      startNewChat={startNewChat}
                      resendUserMessage={resendUserMessage}
                      onRetryConnection={onRetryConnection}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </ChatScroller>
        {/* PromptContainer: Holds the input area for typing new messages */}
        {SplashScreen ? (
          (isUserMessages || dialogueId) && (
            <PromptContainer>{ChatPromptInput}</PromptContainer>
          )
        ) : (
          <PromptContainer>{ChatPromptInput}</PromptContainer>
        )}
      </ChatMainContainer>
    </ChatContext.Provider>
  );
};

export default ChatContainer;
